import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { BiGift } from "react-icons/bi";
import { FiBox } from "react-icons/fi";
import { HiArrowRight, HiOutlineShoppingBag } from "react-icons/hi2";
import Link from "next/link";
import { useParams, usePathname } from "next/navigation";
import { Button, type ButtonProps } from "@/components/ui";
import { isEmbedPath } from "@/lib/embed-links";
import { SHOP_SECTIONS } from "@/lib/shop-links";
import { getShopPath } from "@/lib/shop-links";
import { cx } from "@/lib/tailwind-utils";
import { selfScheduleQueryOptions } from "@/api/check-in/self-schedule/self-schedule-query";
import { tenantQueryOptions } from "@/query/tenant";
const useShopCtaAvailability = () => {
  const $ = _c(15);
  const {
    tenant
  } = useParams();
  let t0;
  if ($[0] !== tenant) {
    t0 = tenantQueryOptions.public(tenant);
    $[0] = tenant;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = {
      ...t0
    };
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    data: publicTenant
  } = useQuery(t1);
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      ...selfScheduleQueryOptions.frontPage(),
      select: _temp
    };
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data: frontPageData
  } = useQuery(t2);
  let t3;
  if ($[5] !== publicTenant?.enabledModules) {
    t3 = publicTenant?.enabledModules?.includes("SHOP") ?? false;
    $[5] = publicTenant?.enabledModules;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const hasShopModule = t3;
  let t4;
  if ($[7] !== hasShopModule || $[8] !== publicTenant?.enabledModules) {
    t4 = (publicTenant?.enabledModules?.includes("GIFT_CARDS") ?? false) && hasShopModule;
    $[7] = hasShopModule;
    $[8] = publicTenant?.enabledModules;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const hasGiftCardShopModule = t4;
  const showGiftCardShopCta = hasGiftCardShopModule && (frontPageData?.hasVisibleGiftCards ?? false);
  const showPackageShopCta = hasShopModule && (frontPageData?.hasVisiblePackages ?? false);
  const t5 = showGiftCardShopCta || showPackageShopCta;
  let t6;
  if ($[10] !== showGiftCardShopCta || $[11] !== showPackageShopCta || $[12] !== t5 || $[13] !== tenant) {
    t6 = {
      tenant,
      showGiftCardShopCta,
      showPackageShopCta,
      showShopCtas: t5
    };
    $[10] = showGiftCardShopCta;
    $[11] = showPackageShopCta;
    $[12] = t5;
    $[13] = tenant;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};
export const ShopItineraryPromptButton = t0 => {
  const $ = _c(16);
  let children;
  let className;
  let href;
  let icon;
  let props;
  if ($[0] !== t0) {
    ({
      href,
      children,
      className,
      icon,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = href;
    $[4] = icon;
    $[5] = props;
  } else {
    children = $[1];
    className = $[2];
    href = $[3];
    icon = $[4];
    props = $[5];
  }
  let t1;
  if ($[6] !== className) {
    t1 = cx("w-full text-h4 text-nowrap font-semibold !px-auto", className);
    $[6] = className;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== children || $[9] !== href) {
    t2 = <Link href={href}>{children}</Link>;
    $[8] = children;
    $[9] = href;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== icon || $[12] !== props || $[13] !== t1 || $[14] !== t2) {
    t3 = <Button asChild={true} className={t1} variant="primary" icon={icon} {...props}>{t2}</Button>;
    $[11] = icon;
    $[12] = props;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
};
export const ShopItineraryPrompt = t0 => {
  const $ = _c(10);
  const {
    className
  } = t0;
  const {
    showGiftCardShopCta,
    showPackageShopCta,
    showShopCtas
  } = useShopCtaAvailability();
  if (!showShopCtas) {
    return null;
  }
  let t1;
  if ($[0] !== className) {
    t1 = cx("space-y-2", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== showGiftCardShopCta) {
    t2 = showGiftCardShopCta && <GiftCardItineraryPrompt />;
    $[2] = showGiftCardShopCta;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== showPackageShopCta) {
    t3 = showPackageShopCta && <PackageItineraryPrompt />;
    $[4] = showPackageShopCta;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <div className={t1}>{t2}{t3}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};

/**
 * Card-style shop upsell matching booking-complete forms / portal prompts.
 * Returns null when gift cards and packages are unavailable.
 */
export const ShopUpsellPrompt = t0 => {
  const $ = _c(27);
  const {
    className
  } = t0;
  const {
    tenant,
    showGiftCardShopCta,
    showPackageShopCta,
    showShopCtas
  } = useShopCtaAvailability();
  if (!showShopCtas || !tenant) {
    return null;
  }
  const showBoth = showGiftCardShopCta && showPackageShopCta;
  const title = showBoth ? "Gift cards & packages available" : showGiftCardShopCta ? "Send a gift card" : "Save with a package";
  const description = showBoth ? "Browse gift cards and treatment packages in our shop." : showGiftCardShopCta ? "Treat someone special \u2014 or yourself \u2014 with a digital gift card." : "Prepay for treatments and save on your next visits.";
  let t1;
  if ($[0] !== showBoth || $[1] !== showGiftCardShopCta || $[2] !== tenant) {
    t1 = showBoth ? getShopPath(tenant) : showGiftCardShopCta ? getShopPath(tenant, SHOP_SECTIONS.giftCards) : getShopPath(tenant, SHOP_SECTIONS.packages);
    $[0] = showBoth;
    $[1] = showGiftCardShopCta;
    $[2] = tenant;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const primaryHref = t1;
  const primaryLabel = showBoth ? "Browse the shop" : showGiftCardShopCta ? "Shop gift cards" : "Shop packages";
  let t2;
  if ($[4] !== className) {
    t2 = cx("overflow-hidden rounded-xl border border-border bg-background text-left", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-foreground/5 text-foreground-muted"><HiOutlineShoppingBag className="h-4 w-4" /></div>;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className="text-caption font-medium uppercase tracking-wide text-foreground-secondary">Shop</p>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== title) {
    t5 = <h3 className="text-body font-semibold leading-tight text-foreground">{title}</h3>;
    $[8] = title;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== description) {
    t6 = <p className="text-caption text-foreground-muted">{description}</p>;
    $[10] = description;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = <div className="flex items-start gap-3 px-4 py-4">{t3}<div className="min-w-0 flex-1 space-y-0.5">{t4}{t5}{t6}</div></div>;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== primaryLabel) {
    t8 = <span>{primaryLabel}</span>;
    $[15] = primaryLabel;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <HiArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t8) {
    t10 = <Button variant="primary" className="group w-full">{t8}{t9}</Button>;
    $[18] = t8;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== primaryHref || $[21] !== t10) {
    t11 = <div className="border-t border-border p-3"><Link href={primaryHref} className="block">{t10}</Link></div>;
    $[20] = primaryHref;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t2 || $[25] !== t7) {
    t12 = <div className={t2}>{t7}{t11}</div>;
    $[23] = t11;
    $[24] = t2;
    $[25] = t7;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
};
export const GiftCardItineraryPrompt = t0 => {
  const $ = _c(6);
  const {
    className
  } = t0;
  const {
    tenant
  } = useParams();
  const pathname = usePathname();
  let t1;
  if ($[0] !== pathname || $[1] !== tenant) {
    t1 = getShopPath(tenant, SHOP_SECTIONS.giftCards, null, isEmbedPath(pathname));
    $[0] = pathname;
    $[1] = tenant;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const href = t1;
  let t2;
  if ($[3] !== className || $[4] !== href) {
    t2 = <ShopItineraryPromptButton href={href} className={className} icon={BiGift}>Shop Gift Cards</ShopItineraryPromptButton>;
    $[3] = className;
    $[4] = href;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
export const PackageItineraryPrompt = t0 => {
  const $ = _c(6);
  const {
    className
  } = t0;
  const {
    tenant
  } = useParams();
  const pathname = usePathname();
  let t1;
  if ($[0] !== pathname || $[1] !== tenant) {
    t1 = getShopPath(tenant, SHOP_SECTIONS.packages, null, isEmbedPath(pathname));
    $[0] = pathname;
    $[1] = tenant;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const href = t1;
  let t2;
  if ($[3] !== className || $[4] !== href) {
    t2 = <ShopItineraryPromptButton href={href} className={className} icon={FiBox}>Shop Packages</ShopItineraryPromptButton>;
    $[3] = className;
    $[4] = href;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
function _temp(data) {
  return {
    hasVisibleGiftCards: data?.hasVisibleGiftCards ?? false,
    hasVisiblePackages: data?.hasVisiblePackages ?? false
  };
}